import { animated } from '@react-spring/web';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import type { RGBColor } from '@sorare/core/src/style/types';

import { useSwitchRewardSpring } from './useSwitchRewardSpring.ts';

const Relative = styled(animated.div)`
  position: relative;
  overflow: hidden;
`;
const Current = styled.div`
  .animate & {
    clip-path: rect(auto auto var(--y) auto);
  }
`;
const Previous = styled.div`
  position: absolute;
  inset: 0;

  .animate & {
    clip-path: rect(var(--y) auto auto auto);
  }
`;
const Bar = styled.div`
  position: absolute;
  z-index: 1;

  top: -50px;
  .animate & {
    top: calc(var(--y) - 15px);
  }

  width: 100%;
  height: 30px;
  background: radial-gradient(
    70% 50% at 50% 50%,
    var(--c-white) 5%,
    rgb(var(--rgb-color)) 10%,
    rgb(var(--rgb-color)) 30%,
    transparent
  );
`;

type Props<T> = {
  rewardConfig?: T | null;
  previousRewardConfig?: T | null;
  children: ({ config }: { config?: T | null }) => ReactNode;
  revealed?: boolean;
  rgbColor: RGBColor | null;
};
export const RewardSwitch = <T extends { id: string }>({
  rewardConfig,
  previousRewardConfig,
  children,
  revealed,
  rgbColor,
}: Props<T>) => {
  const [revealedSince400ms, setRevealedSince400ms] = useState(false);

  useEffect(() => {
    const timeout = revealed
      ? setTimeout(() => setRevealedSince400ms(true), 400)
      : undefined;
    return () => clearTimeout(timeout);
  }, [revealed]);

  const { y, animate } = useSwitchRewardSpring({
    rewardConfig,
    previousRewardConfig,
  });

  return (
    <Relative
      className={classNames({
        animate,
      })}
      style={
        {
          '--y': y.to(n => `${n}%`),
        } as CSSProperties
      }
    >
      {!!revealedSince400ms && (
        <Previous>{children({ config: previousRewardConfig })}</Previous>
      )}
      <Current>{children({ config: rewardConfig })}</Current>
      <Bar
        style={
          { '--rgb-color': rgbColor || 'var(--c-limited)' } as CSSProperties
        }
      />
    </Relative>
  );
};
